import PREDEFINED_INTERVALS from '../../../../../shared/constants/predefinedIntervals';
import convertLocalToUTCISOString from '../../../../../shared/utils/convertLocalToUTCISOString';
import isValidDate from '../../../../../shared/utils/isValidDate';
import toUTCStartOfDay from '../../../../../shared/utils/toUTCStartOfDay';
import { REPORT_PARAMS } from '../constants';
import { createControlForParam, createControlForParamReportTask } from './createControlForParam';

export const initControlsFields = (listParams) => {
  const newControls = [];
  listParams?.forEach((param) => {
    const newControl = createControlForParam(param);
    if (newControl) {
      newControls.push(newControl);
    }
  });
  return newControls.sort((a, b) => a.orderNum - b.orderNum);
};

export const initControlsFromReportTask = (listParams) => {
  const newControls = [];
  listParams?.forEach((param) => {
    const newControl = createControlForParamReportTask(param.param, param.value);
    if (newControl) {
      newControls.push(newControl);
    }
  });
  return newControls.sort((a, b) => a.orderNum - b.orderNum);
};

export const toDtoValue = (value, utc) => {
  switch (value.type) {
    case REPORT_PARAMS.TEXT:
      return value.value;
    case REPORT_PARAMS.NUMERIC:
      return Number(value.value);
    case REPORT_PARAMS.DATETIME:
      return isValidDate(value.value) ? convertLocalToUTCISOString(value.value.toISOString(), utc) : null;
    case REPORT_PARAMS.DATE:
      return isValidDate(value.value) ? convertLocalToUTCISOString(value.value.toISOString(), utc) : null;
    case REPORT_PARAMS.INTERVAL:
    case REPORT_PARAMS.DATE_INTERVAL: {
      const [start, finish] = value.value.split(/-(?=\d{4}-\d{2}-\d{2}T)/);

      return `${convertLocalToUTCISOString(start, utc)}-${convertLocalToUTCISOString(finish, utc)}`;
    }
    case REPORT_PARAMS.USER:
      return value.value?.appUserId;
    case REPORT_PARAMS.DIVISION:
      return value.value?.divisionId;
    default:
      return null;
  }
};

export const isValidField = (item) => {
  switch (item.type) {
    case REPORT_PARAMS.TEXT:
    case REPORT_PARAMS.NUMERIC:
      return item.isRequired ? !!item.value?.trim() : true;
    case REPORT_PARAMS.DATETIME:
    case REPORT_PARAMS.DATE:
      return item.isRequired ? !!isValidDate(item.value) : true;
    case REPORT_PARAMS.INTERVAL:
    case REPORT_PARAMS.DATE_INTERVAL:
      return item.isRequired ? !!item.value?.trim() : true;
    case REPORT_PARAMS.DIVISION:
    case REPORT_PARAMS.USER:
    case REPORT_PARAMS.VEHICLE:
      return item.isRequired ? !!item.value : true;
    default:
      return false;
  }
};

export function getRegularIntervalValues(selectedInterval, dateOnly) {
  let fromDate;
  let toDate;
  const today = new Date();

  switch (selectedInterval) {
    case PREDEFINED_INTERVALS.TODAY:
      fromDate = new Date();
      fromDate.setHours(0, 0, 0, 0);
      toDate = new Date();
      toDate.setDate(toDate.getDate() + 1);
      toDate.setHours(0, 0, 0, 0);
      break;
    case PREDEFINED_INTERVALS.YESTERDAY:
      fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 1);
      fromDate.setHours(0, 0, 0, 0);
      toDate = new Date();
      toDate.setHours(0, 0, 0, 0);
      break;
    case PREDEFINED_INTERVALS.DAY:
      fromDate = new Date();
      fromDate.setHours(fromDate.getHours() - 24);
      toDate = new Date();
      if (dateOnly) {
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
      }
      break;
    case PREDEFINED_INTERVALS.WEEK:
      fromDate = new Date();
      fromDate.setHours(fromDate.getHours() - 24 * (dateOnly ? 6 : 7));
      toDate = new Date();
      if (dateOnly) {
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
      }
      break;
    case PREDEFINED_INTERVALS.CURRENT_MONTH:
      toDate = new Date();
      fromDate = new Date(toDate.getFullYear(), toDate.getMonth(), 1, 0, 0, 0, 0);
      if (dateOnly) toDate.setHours(0, 0, 0, 0);
      break;
    case PREDEFINED_INTERVALS.LAST_MONTH:
      fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1, 0, 0, 0, 0);
      toDate = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
      break;
    case PREDEFINED_INTERVALS.CUSTOM:
    default:
      return null;
  }
  return [fromDate.toISOString(), toDate.toISOString()];
}

export const getIntervalValue = (from, to, fromError, toError, isSchedule) => {
  if (!isSchedule) {
    return isValidDate(new Date(from)) && isValidDate(new Date(to)) && !fromError && !toError
      ? `${toUTCStartOfDay(from)}-${toUTCStartOfDay(to)}`
      : '';
  }
  return typeof from === 'string' && typeof to === 'string' ? `${from}, ${to}` : '';
};

const specialFields = ['divisionId', 'appUserId'];
export const prepareControls = (controls) => {
  return controls.map((control) => ({
    param: { reportParamId: control.reportParamId },
    value:
      specialFields.includes(control.code) && control.value?.[control.code]
        ? control.value[control.code]
        : control.value || control.defaultValue,
  }));
};

export function validateReportTaskForm({ selectedType, taskName, schedule, controls, recipients, startDate }) {
  if (!selectedType || selectedType.trim() === '') return false;
  if (!taskName || taskName.trim() === '') return false;
  if (!schedule || schedule.trim() === '') return false;
  if (!startDate) return false;

  const hasInvalidControls = controls.some((c) => c.isRequired && (!c.value || String(c.value).trim() === ''));
  if (hasInvalidControls) return false;

  if (!recipients.length) return false;

  return true;
}

export const prepareRecipients = (recipients) => {
  return recipients.map((recipient) => ({ appUserId: recipient.appUserId }));
};
